import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

interface DTExpandableTextProps {
  value: string;
  previewLength?: number;
  responsivePreviewLength?: { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };
  sx?: object;
  codeStyle?: boolean;
  showToggle?: boolean;
  showToggleLabel?: boolean;
  showMoreLabel?: string;
  showLessLabel?: string;
  maxWidth?: number | string;
  fontSize?: string | number;
  color?: string;
  background?: string;
  border?: string;
}

const DtExpandableText: React.FC<DTExpandableTextProps> = ({
  value,
  previewLength,
  responsivePreviewLength,
  sx,
  showToggle = true,
  showToggleLabel = true,
  showMoreLabel = "Show more",
  showLessLabel = "Show less",
  maxWidth = "100%",
  fontSize = "0.95rem",
  color,
  background,
  border,
}) => {
  const theme = useTheme();

  // Breakpoint booleans
  const isXl = useMediaQuery(theme.breakpoints.up("xl"));
  const isLg = useMediaQuery(theme.breakpoints.up("lg"));
  const isMd = useMediaQuery(theme.breakpoints.up("md"));
  const isSm = useMediaQuery(theme.breakpoints.up("sm"));
  const isXs = useMediaQuery(theme.breakpoints.up("xs"));

  // Detect if caller explicitly provided preview behavior
  const hasExplicitPreview =
    previewLength !== undefined || responsivePreviewLength !== undefined;

  // Responsive preview length (only used when explicit preview is configured)
  let effectivePreviewLength = previewLength ?? 80; // fallback if they only provided responsive or nothing but still "explicit" mode

  if (responsivePreviewLength) {
    if (isXl && responsivePreviewLength.xl !== undefined) {
      effectivePreviewLength = responsivePreviewLength.xl;
    } else if (isLg && responsivePreviewLength.lg !== undefined) {
      effectivePreviewLength = responsivePreviewLength.lg;
    } else if (isMd && responsivePreviewLength.md !== undefined) {
      effectivePreviewLength = responsivePreviewLength.md;
    } else if (isSm && responsivePreviewLength.sm !== undefined) {
      effectivePreviewLength = responsivePreviewLength.sm;
    } else if (isXs && responsivePreviewLength.xs !== undefined) {
      effectivePreviewLength = responsivePreviewLength.xs;
    }
  }

  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const textRef = useRef<HTMLSpanElement | null>(null);

  // For explicit char-based preview mode
  const preview =
    hasExplicitPreview && value.length > effectivePreviewLength
      ? value.slice(0, effectivePreviewLength) + "..."
      : value;

  // Auto one-line detection mode (only when NO previewLength / responsivePreviewLength passed)
  useEffect(() => {
    if (!textRef.current || hasExplicitPreview) return;

    const el = textRef.current;

    const checkOverflow = () => {
      // Compare scrollHeight and clientHeight to see if truncation/overflow happens
      // Add 1px tolerance to account for sub-pixel rendering differences and browser rounding errors
      const overflowing = el.scrollHeight > el.clientHeight + 1;
      setIsOverflowing(overflowing);
    };

    checkOverflow();

    // React to resize / font changes
    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });

    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
    };
  }, [value, fontSize, hasExplicitPreview]);

  const handleToggle = useCallback(
    (e?: React.MouseEvent) => {
      if (e) e.stopPropagation();
      setExpanded((prev) => !prev);
    },
    []
  );

  // Decide when to show toggle:
  // - Explicit preview mode → if text longer than previewLength
  // - Auto one-line mode → if actually overflowing OR currently expanded (so user can collapse)
  const shouldShowToggle =
    showToggle &&
    (hasExplicitPreview
      ? value.length > effectivePreviewLength
      : isOverflowing || expanded);

  const handleTextClick = () => {
    if (!shouldShowToggle) return;
    handleToggle();
  };

  return (
    <Box
      sx={{
        maxWidth,
        ...sx,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <Typography
          component="span"
          ref={textRef}
          variant="body1"
          sx={{
            fontSize,
            background: background,
            wordBreak: "break-all",
            whiteSpace: "pre-line",
            border: border,
            color: color ?? theme.palette.text.primary,
            cursor: shouldShowToggle ? "pointer" : "default",
            flex: 1,
            // AUTO ONE-LINE MODE (no explicit preview): clamp to 1 line when collapsed
            ...(hasExplicitPreview
              ? {}
              : !expanded
              ? {
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }
              : {
                  // expanded → show full text
                  display: "block",
                }),
          }}
          title={value}
          onClick={handleTextClick}
        >
          {hasExplicitPreview ? (expanded ? value : preview) : value}
        </Typography>

        {shouldShowToggle &&
          (showToggleLabel ? (
            <Box
              sx={{
                position: "absolute",
                right: 8,
                bottom: 0,
                fontSize: "0.85rem",
                color: theme.palette.primary.main,
                cursor: "pointer",
                userSelect: "none",
                zIndex: 100,
                background: background || theme.palette.background.default,
                borderRadius: "25%",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 0.5,
                py: 0.25,
              }}
              onClick={handleToggle}
            >
              {expanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
              {expanded ? showLessLabel : showMoreLabel}
            </Box>
          ) : (
            <Box
              sx={{
                fontSize: "0.85rem",
                color: theme.palette.primary.main,
                cursor: "pointer",
                userSelect: "none",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
              onClick={handleToggle}
            >
              {expanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </Box>
          ))}
      </Box>
    </Box>
  );
};

export { DtExpandableText };
